import { useState, useCallback, useMemo, useRef } from 'react';
import { cn } from '@/lib/utils/cn';
import { QuestionWrapper } from './question-wrapper';
import type { QuestionComponentProps } from '@/types';

type SliderStyle = 'continuous' | 'stepped' | 'segmented';
type SliderSize = 'small' | 'medium' | 'large';

interface SliderSegment {
  count: number;
  labels?: string[];
  colors?: string[];
}

interface SliderGradient {
  enabled: boolean;
  startColor: string;
  endColor: string;
}

interface SliderConfig {
  // Scale settings
  minValue?: number;
  maxValue?: number;
  step?: number;
  defaultValue?: number;
  startEmpty?: boolean;
  minLabel?: string;
  maxLabel?: string;
  midLabel?: string;
  showLabels?: boolean;
  showValue?: boolean;
  showTicks?: boolean;
  tickInterval?: number;
  tickLabels?: string[];
  // Style
  sliderStyle?: SliderStyle;
  segments?: SliderSegment;
  // Display
  trackColor?: string;
  fillColor?: string;
  thumbColor?: string;
  size?: SliderSize;
  showTooltip?: boolean;
  tooltipFormat?: string;
  showInputField?: boolean;
  gradient?: SliderGradient;
}

const SIZE_CONFIG = {
  small: { trackHeight: 4, thumbSize: 16 },
  medium: { trackHeight: 8, thumbSize: 20 },
  large: { trackHeight: 12, thumbSize: 28 },
} as const;

export function SliderQuestion({
  question,
  value,
  onChange,
  disabled,
  errors,
}: QuestionComponentProps<number | null>) {
  const config = question.config as SliderConfig | undefined;

  // Scale settings
  const minValue = config?.minValue ?? 0;
  const maxValue = config?.maxValue ?? 100;
  const step = config?.step ?? 1;
  const defaultValue = config?.defaultValue;
  const startEmpty = config?.startEmpty ?? false;
  const minLabel = config?.minLabel;
  const maxLabel = config?.maxLabel;
  const midLabel = config?.midLabel;
  const showLabels = config?.showLabels ?? true;
  const showTicks = config?.showTicks ?? false;
  const tickInterval = config?.tickInterval;
  const tickLabels = config?.tickLabels;

  // Style settings
  const sliderStyle = config?.sliderStyle || 'continuous';
  const segments = config?.segments;

  // Display settings
  const showTooltip = config?.showTooltip ?? true;
  const showValue = config?.showValue ?? true;
  const showInputField = config?.showInputField ?? false;
  const trackColor = config?.trackColor || '#E5E7EB';
  const fillColor = config?.fillColor || '#3B82F6';
  const thumbColor = config?.thumbColor || '#3B82F6';
  const size = config?.size || 'medium';
  const tooltipFormat = config?.tooltipFormat;
  const gradient = config?.gradient;

  const { trackHeight, thumbSize } = SIZE_CONFIG[size];
  const trackRef = useRef<HTMLDivElement>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [hoverValue, setHoverValue] = useState<number | null>(null);
  const [inputValue, setInputValue] = useState<string>('');

  // Determine if slider has been touched/has a value
  const hasValue = value !== null && value !== undefined;

  // Calculate initial value
  const initialValue = useMemo(() => {
    if (hasValue) return value;
    if (startEmpty) return null;
    if (defaultValue !== undefined) return defaultValue;
    return minValue;
  }, [hasValue, value, startEmpty, defaultValue, minValue]);

  const currentValue = initialValue;
  const displayValue = hoverValue ?? currentValue ?? minValue;

  // Calculate percentage for fill
  const percentage = useMemo(() => {
    if (currentValue === null) return 0;
    return ((currentValue - minValue) / (maxValue - minValue)) * 100;
  }, [currentValue, minValue, maxValue]);

  // Format value for display
  const formatValue = useCallback((val: number) => {
    if (!tooltipFormat) return String(val);
    return tooltipFormat.replace('{value}', String(val));
  }, [tooltipFormat]);

  // Get segment for a value (NPS-style)
  const getValueSegment = useCallback((val: number): number => {
    if (!segments || segments.count < 2) return 0;
    const range = maxValue - minValue;
    const segmentSize = range / segments.count;
    const segment = Math.floor((val - minValue) / segmentSize);
    return Math.min(segment, segments.count - 1);
  }, [segments, minValue, maxValue]);

  // Get segment color for a value
  const getSegmentColor = useCallback((val: number): string => {
    if (!segments?.colors || segments.colors.length === 0) return fillColor;
    const segmentIndex = getValueSegment(val);
    return segments.colors[segmentIndex] || fillColor;
  }, [segments, fillColor, getValueSegment]);

  // Generate tick marks
  const ticks = useMemo(() => {
    if (!showTicks) return [];

    const interval = tickInterval || step;
    const range = maxValue - minValue;
    const tickCount = Math.floor(range / interval) + 1;

    // Limit ticks to prevent UI clutter
    const maxTicks = 21;
    const actualInterval = tickCount > maxTicks ? range / (maxTicks - 1) : interval;
    const actualTickCount = Math.min(tickCount, maxTicks);

    return Array.from({ length: actualTickCount }, (_, i) => {
      const tickValue = minValue + i * actualInterval;
      const snappedValue = Math.round(tickValue / step) * step;
      const position = ((snappedValue - minValue) / range) * 100;

      // Use custom label if available
      const label = tickLabels?.[i] ?? String(Math.round(snappedValue));

      return {
        value: snappedValue,
        position: Math.min(100, Math.max(0, position)),
        label,
      };
    });
  }, [showTicks, minValue, maxValue, step, tickInterval, tickLabels]);

  // Generate segment backgrounds for segmented style
  const segmentBackgrounds = useMemo(() => {
    if (sliderStyle !== 'segmented' || !segments || segments.count < 2) return null;

    const segmentWidth = 100 / segments.count;
    return Array.from({ length: segments.count }, (_, i) => ({
      width: segmentWidth,
      left: i * segmentWidth,
      color: segments.colors?.[i] || fillColor,
      label: segments.labels?.[i],
    }));
  }, [sliderStyle, segments, fillColor]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const numValue = parseFloat(e.target.value);
    onChange(numValue);
  }, [onChange]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);

    const numValue = parseFloat(val);
    if (!isNaN(numValue) && numValue >= minValue && numValue <= maxValue) {
      // Snap to step
      const snapped = Math.round(numValue / step) * step;
      onChange(snapped);
    }
  }, [minValue, maxValue, step, onChange]);

  const handleInputBlur = useCallback(() => {
    if (currentValue !== null) {
      setInputValue(String(currentValue));
    }
  }, [currentValue]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
    if (disabled || isDragging) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = x / rect.width;
    const rawValue = minValue + pct * (maxValue - minValue);
    const snappedValue = Math.round(rawValue / step) * step;
    setHoverValue(Math.max(minValue, Math.min(maxValue, snappedValue)));
  }, [disabled, isDragging, minValue, maxValue, step]);

  const handleMouseLeave = useCallback(() => {
    if (!isDragging) {
      setHoverValue(null);
    }
  }, [isDragging]);

  // Calculate gradient fill style
  const getFillStyle = useCallback(() => {
    if (currentValue === null) return { width: '0%' };

    const width = `${percentage}%`;

    if (gradient?.enabled && gradient.startColor && gradient.endColor) {
      return {
        width,
        background: `linear-gradient(to right, ${gradient.startColor}, ${gradient.endColor})`,
      };
    }

    if (sliderStyle === 'segmented' && segments?.colors) {
      const segmentColor = getSegmentColor(currentValue);
      return {
        width,
        backgroundColor: segmentColor,
      };
    }

    return {
      width,
      backgroundColor: fillColor,
    };
  }, [currentValue, percentage, gradient, sliderStyle, segments, fillColor, getSegmentColor]);

  // Render stepped/segmented track marks
  const renderStepMarks = () => {
    if (sliderStyle !== 'stepped') return null;

    const range = maxValue - minValue;
    const stepCount = Math.floor(range / step);

    // Don't render too many marks
    if (stepCount > 50) return null;

    return (
      <div className="absolute inset-0 flex items-center pointer-events-none">
        {Array.from({ length: stepCount + 1 }, (_, i) => {
          const pos = (i / stepCount) * 100;
          return (
            <div
              key={i}
              className="absolute w-0.5 bg-white/50 rounded"
              style={{
                left: `${pos}%`,
                height: trackHeight * 0.6,
                transform: 'translateX(-50%)',
              }}
            />
          );
        })}
      </div>
    );
  };

  // Render segmented backgrounds
  const renderSegmentedBackground = () => {
    if (!segmentBackgrounds) return null;

    return (
      <div className="absolute inset-0 flex rounded-full overflow-hidden">
        {segmentBackgrounds.map((seg, i) => (
          <div
            key={i}
            className="h-full opacity-20"
            style={{
              width: `${seg.width}%`,
              backgroundColor: seg.color,
            }}
          />
        ))}
      </div>
    );
  };

  // Render segment labels (for NPS-style)
  const renderSegmentLabels = () => {
    if (!segmentBackgrounds || !segments?.labels) return null;

    return (
      <div className="flex justify-between mt-2 text-xs">
        {segmentBackgrounds.map((seg, i) => (
          <div
            key={i}
            className="text-center px-1"
            style={{ width: `${seg.width}%`, color: seg.color }}
          >
            {seg.label}
          </div>
        ))}
      </div>
    );
  };

  const thumbStyles = cn(
    '[&::-webkit-slider-thumb]:appearance-none',
    '[&::-webkit-slider-thumb]:rounded-full',
    '[&::-webkit-slider-thumb]:bg-white',
    '[&::-webkit-slider-thumb]:border-2',
    '[&::-webkit-slider-thumb]:shadow-md',
    '[&::-webkit-slider-thumb]:cursor-pointer',
    '[&::-webkit-slider-thumb]:transition-transform',
    '[&::-webkit-slider-thumb]:hover:scale-110',
    '[&::-moz-range-thumb]:rounded-full',
    '[&::-moz-range-thumb]:bg-white',
    '[&::-moz-range-thumb]:border-2',
    '[&::-moz-range-thumb]:cursor-pointer'
  );

  return (
    <QuestionWrapper question={question} errors={errors}>
      <div className="max-w-xl space-y-2">
        {/* Empty state indicator */}
        {startEmpty && !hasValue && (
          <p className="text-sm text-gray-500 italic mb-2">
            Drag the slider to select a value
          </p>
        )}

        {/* Main slider container */}
        <div className="relative pt-8 pb-2">
          {/* Tooltip */}
          {showTooltip && (isDragging || hoverValue !== null) && currentValue !== null && (
            <div
              className="absolute -top-1 transform -translate-x-1/2 bg-gray-900 text-white text-sm px-2 py-1 rounded shadow-lg transition-opacity z-10 whitespace-nowrap"
              style={{ left: `${percentage}%` }}
            >
              {formatValue(displayValue)}
              <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
            </div>
          )}

          {/* Track container */}
          <div
            ref={trackRef}
            className="relative rounded-full"
            style={{
              height: trackHeight,
              backgroundColor: trackColor,
            }}
          >
            {/* Segmented background (for NPS style) */}
            {renderSegmentedBackground()}

            {/* Fill */}
            <div
              className="absolute h-full rounded-full transition-all"
              style={getFillStyle()}
            />

            {/* Step marks for stepped style */}
            {renderStepMarks()}
          </div>

          {/* Native range input */}
          <input
            type="range"
            value={currentValue ?? minValue}
            onChange={handleChange}
            onMouseDown={() => setIsDragging(true)}
            onMouseUp={() => setIsDragging(false)}
            onTouchStart={() => setIsDragging(true)}
            onTouchEnd={() => setIsDragging(false)}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            min={minValue}
            max={maxValue}
            step={step}
            disabled={disabled}
            className={cn(
              'absolute inset-0 w-full appearance-none bg-transparent cursor-pointer',
              'focus:outline-none',
              thumbStyles,
              disabled && 'opacity-50 cursor-not-allowed'
            )}
            style={{
              top: '50%',
              transform: 'translateY(-50%)',
              height: trackHeight,
              // Dynamic thumb size
              ['--thumb-size' as string]: `${thumbSize}px`,
              ['--thumb-border-color' as string]: thumbColor,
            }}
            aria-label={question.text}
            aria-valuemin={minValue}
            aria-valuemax={maxValue}
            aria-valuenow={currentValue ?? undefined}
          />

          {/* Custom thumb size styling */}
          <style>{`
            input[type="range"]::-webkit-slider-thumb {
              width: ${thumbSize}px;
              height: ${thumbSize}px;
              border-color: ${thumbColor};
              margin-top: -${(thumbSize - trackHeight) / 2}px;
            }
            input[type="range"]::-moz-range-thumb {
              width: ${thumbSize}px;
              height: ${thumbSize}px;
              border-color: ${thumbColor};
            }
          `}</style>

          {/* Tick marks */}
          {showTicks && ticks.length > 0 && (
            <div className="absolute w-full" style={{ top: trackHeight + 4 }}>
              {ticks.map((tick, i) => (
                <div
                  key={i}
                  className="absolute flex flex-col items-center"
                  style={{ left: `${tick.position}%`, transform: 'translateX(-50%)' }}
                >
                  <div className="w-0.5 h-2 bg-gray-300" />
                  <span className="text-xs text-gray-500 mt-1 whitespace-nowrap">{tick.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Segment labels (NPS style) */}
        {sliderStyle === 'segmented' && renderSegmentLabels()}

        {/* Labels row */}
        {showLabels && (minLabel || maxLabel || midLabel) && (
          <div className={cn(
            'flex justify-between text-xs text-gray-500',
            showTicks && ticks.length > 0 && 'mt-8'
          )}>
            <span>{minLabel}</span>
            {midLabel && <span>{midLabel}</span>}
            <span>{maxLabel}</span>
          </div>
        )}

        {/* Value display and input */}
        <div className={cn(
          'flex items-center gap-4',
          showInputField ? 'justify-between' : 'justify-center'
        )}>
          {/* Current value display */}
          {showValue && !showTooltip && currentValue !== null && (
            <div className="text-center">
              <span
                className="text-lg font-semibold"
                style={{ color: sliderStyle === 'segmented' ? getSegmentColor(currentValue) : fillColor }}
              >
                {formatValue(currentValue)}
              </span>
            </div>
          )}

          {/* Numeric input field */}
          {showInputField && (
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-500">Value:</label>
              <input
                type="number"
                value={inputValue || (currentValue !== null ? currentValue : '')}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onFocus={() => currentValue !== null && setInputValue(String(currentValue))}
                min={minValue}
                max={maxValue}
                step={step}
                disabled={disabled}
                className={cn(
                  'w-20 px-2 py-1 text-center border border-gray-300 rounded-md',
                  'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                  disabled && 'opacity-50 cursor-not-allowed bg-gray-100'
                )}
              />
            </div>
          )}
        </div>
      </div>
    </QuestionWrapper>
  );
}
